/**
 * Marketing-Attribution: UTM-/fbclid-/gclid-Parameter aus der URL beim
 * ersten Seitenaufruf in den sessionStorage schreiben, damit sie über
 * mehrere Routen-Wechsel hinweg bis zur Registrierung erhalten bleiben.
 *
 * Wichtig: Erst-Capture gewinnt – einmal gesetzte Werte werden in derselben
 * Browser-Session NICHT überschrieben. Kein Referrer-Check (Meta/Facebook
 * verschluckt den Referrer regelmäßig).
 */

import { safeSessionGetItem, safeSessionSetItem } from "@/lib/safeStorage";

export interface Attribution {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  fbclid: string | null;
  gclid: string | null;
  landing_path: string | null;
  referrer: string | null;
}

export const ATTRIBUTION_STORAGE_KEY = "mk_attribution";

const EMPTY: Attribution = {
  utm_source: null,
  utm_medium: null,
  utm_campaign: null,
  utm_content: null,
  utm_term: null,
  fbclid: null,
  gclid: null,
  landing_path: null,
  referrer: null,
};

function pick(params: URLSearchParams, key: string): string | null {
  const value = params.get(key);
  if (!value) return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed.slice(0, 500) : null;
}

/**
 * Liest UTM-/fbclid-Parameter aus der gegebenen URL und persistiert sie –
 * aber nur, wenn es noch keine gespeicherte Attribution gibt oder die
 * neue URL trackbare Marketing-Parameter enthält (kein Überschreiben
 * durch interne Navigation ohne Parameter).
 */
export function captureAttributionFromUrl(
  url: string = typeof window !== "undefined" ? window.location.href : "",
  referrer: string = typeof document !== "undefined" ? document.referrer : "",
): Attribution {
  if (!url) return EMPTY;

  const existing = getAttribution();
  // Erst-Capture gewinnt
  if (existing) return existing;

  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return EMPTY;
  }

  const params = parsed.searchParams;
  const utm_source = pick(params, "utm_source");
  const utm_medium = pick(params, "utm_medium");
  const utm_campaign = pick(params, "utm_campaign");
  const utm_content = pick(params, "utm_content");
  const utm_term = pick(params, "utm_term");
  const fbclid = pick(params, "fbclid");
  const gclid = pick(params, "gclid");

  // Nur speichern, wenn mindestens ein Marketing-Parameter vorhanden ist.
  const hasMarketingSignal =
    utm_source || utm_medium || utm_campaign || utm_content || utm_term || fbclid || gclid;

  if (!hasMarketingSignal) return EMPTY;

  const attribution: Attribution = {
    utm_source,
    utm_medium,
    utm_campaign,
    utm_content,
    utm_term,
    fbclid,
    gclid,
    landing_path: parsed.pathname || null,
    referrer: referrer ? referrer.slice(0, 500) : null,
  };

  try {
    safeSessionSetItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(attribution));
  } catch {
    /* sessionStorage darf nie blockieren */
  }

  return attribution;
}

export function getAttribution(): Attribution | null {
  const raw = safeSessionGetItem(ATTRIBUTION_STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<Attribution>;
    return { ...EMPTY, ...parsed };
  } catch {
    return null;
  }
}

export const ATTRIBUTION_FELDER = Object.keys(EMPTY) as (keyof Attribution)[];

/**
 * Herkunft für `supabase.auth.signUp` (`options.data`): Der Trigger `profiles_herkunft_aus_anmeldung` übernimmt sie
 * beim Anlegen des Profils in leere Felder, auch wenn zuerst die E-Mail bestätigt werden muss (dann gibt es keine
 * Sitzung, und das Update aus dem Browser läuft nicht). Nur gesetzte Felder; ohne Herkunft ein leeres Objekt.
 */
export function herkunftFuerAnmeldung(): { herkunft?: Partial<Record<keyof Attribution, string>> } {
  const attr = getAttribution();
  if (!attr) return {};
  const herkunft: Partial<Record<keyof Attribution, string>> = {};
  for (const feld of ATTRIBUTION_FELDER) {
    const wert = attr[feld];
    if (typeof wert === "string" && wert.trim()) herkunft[feld] = wert.trim().slice(0, 500);
  }
  return Object.keys(herkunft).length > 0 ? { herkunft } : {};
}

export function clearAttribution(): void {
  try {
    sessionStorage.removeItem(ATTRIBUTION_STORAGE_KEY);
  } catch {
    /* noop */
  }
}
